import React from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, X, AlertTriangle, AlertOctagon, Bot } from 'lucide-react';

export default function PendingBotTasksApproval({ tasks, onApproveAll, onRejectAll }) {
  const queryClient = useQueryClient();

  const approveTaskMutation = useMutation({
    mutationFn: (taskId) => {
      const task = tasks.find(t => t.id === taskId);
      return base44.entities.DailyTask.update(taskId, {
        ...task,
        status: 'approved'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
    }
  });

  const rejectTaskMutation = useMutation({
    mutationFn: (taskId) => base44.entities.DailyTask.delete(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
    }
  });

  if (tasks.length === 0) return null;

  return (
    <Card className="glass-panel border-purple-500/50 bg-purple-900/20 mb-4">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-semibold text-purple-300">
              {tasks.length} AI-Generated Task{tasks.length > 1 ? 's' : ''} Pending Approval
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onApproveAll}
              className="bg-emerald-600 hover:bg-emerald-700 text-white border-none h-7 text-xs">
              <Check className="w-3 h-3 mr-1" />
              Approve All
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onRejectAll}
              className="bg-red-600 hover:bg-red-700 text-white border-none h-7 text-xs">
              <X className="w-3 h-3 mr-1" />
              Reject All
            </Button>
          </div>
        </div>

        {tasks.map((task) => (
          <div key={task.id} className="p-3 bg-slate-900/50 rounded-lg border border-purple-500/30">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1">
                <p className="text-sm text-slate-200 mb-1">{task.task_description}</p>
                {task.key_result_focus && (
                  <p className="text-xs text-slate-400">→ {task.key_result_focus}</p>
                )}
              </div>
              <div className="flex gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => approveTaskMutation.mutate(task.id)}
                  className="h-6 w-6 text-emerald-400 hover:text-emerald-300"
                  title="Approve task">
                  <Check className="w-3 h-3" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => rejectTaskMutation.mutate(task.id)}
                  className="h-6 w-6 text-red-400 hover:text-red-300"
                  title="Reject task">
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>

            {task.ai_assessment && task.ai_assessment.type !== 'ok' && (
              <div className={`text-[10px] p-1.5 rounded-md flex items-start gap-1 ${
                task.ai_assessment.type === 'redundant' 
                  ? 'bg-red-500/10 border border-red-500/30 text-red-300'
                  : 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-300'
              }`}>
                {task.ai_assessment.type === 'redundant' ? (
                  <AlertOctagon className="w-3 h-3 mt-0.5 shrink-0" />
                ) : (
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                )}
                <span className="leading-tight">{task.ai_assessment.message}</span>
              </div>
            )}

            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="text-xs bg-slate-800/50 text-slate-400 border-slate-600">
                {task.time_allocation_percent}% allocation
              </Badge>
              <Badge variant="outline" className="text-xs bg-slate-800/50 text-slate-400 border-slate-600">
                Priority #{task.task_order}
              </Badge>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}